import React, { useEffect, useState } from "react";
import PageHeaderLayout from "../../../../components/Layout/PageHeaderLayout";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowBack, Edit } from "@mui/icons-material";
import { PermissionGuard } from "../../../../components/PermissionGuard";
import { ItemServices } from "./ItemServices";
import Swal from "sweetalert2";
import Loading from "../../../../components/Loader/Loading";
import { formatDateTime } from "../../../../utils/dateUtils";
import { useDispatch } from "react-redux";

const actionTypeColors = {
  navigate: { bg: "#e3f2fd", color: "#1565c0" },
  open_service: { bg: "#f3e5f5", color: "#7b1fa2" },
  open_category: { bg: "#e8f5e9", color: "#2e7d32" },
  open_url: { bg: "#fff3e0", color: "#e65100" },
  show_popup: { bg: "#fce4ec", color: "#c2185b" },
  none: { bg: "#f5f5f5", color: "#757575" },
};

const ViewSectionItem = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const defaultPageType = searchParams.get("pageType") || "section";
  const menuDispatch = useDispatch();
  useEffect(() => {
    menuDispatch({
      type: "menuSelect",
      payload: "home-screen",
    });
  }, []);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [itemData, setItemData] = useState({});
  const [sectionData, setSectionData] = useState({});
  const itemPageType = itemData?.section?.pageType || defaultPageType;
  const entityLabel = itemPageType === "header" ? "Header Item" : itemPageType === "footer" ? "Footer Item" : "Section Item";
  const entityListLabel = itemPageType === "header" ? "Header Items" : itemPageType === "footer" ? "Footer Items" : "Section Items";
  const sectionItemsPath = itemData?.sectionId
    ? `/customer-app/home-screen/section-items/${itemData.sectionId}?pageType=${itemPageType}`
    : `/customer-app/home-screen/section-items?pageType=${itemPageType}`;

  useEffect(() => {
    if (id) {
      fetchItemData();
    }
  }, [id]);

  const fetchItemData = async () => {
    setIsLoading(true);
    try {
      const response = await ItemServices.getItemById(id);
      if (response.success == "1") {
        setItemData(response.data);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to fetch item details",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderDisplayOptions = () => {
    const options = itemData.displayOptions || {};
    return (
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
            Show Title:{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: options.showTitle ? "green" : "red",
              }}
            >
              {options.showTitle ? "Yes" : "No"}
            </Box>
            {options.showTitle && ` (${options.titlePosition || "left"})`}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
            Show Subtitle:{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: options.showSubTitle ? "green" : "red",
              }}
            >
              {options.showSubTitle ? "Yes" : "No"}
            </Box>
            {options.showSubTitle && ` (${options.subTitlePosition || "left"})`}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
              Show Description: <Box component="span" sx={{ fontWeight: 600, color: options.showDescription ? 'green' : 'red' }}>
                  {options.showDescription ? 'Yes' : 'No'}
              </Box>
              {options.showDescription && ` (${options.descriptionPosition || 'left'})`}
          </Typography>
      </Grid>
      </Grid>
    );
  };

  const renderActionData = () => {
    const { actionType, actionData } = itemData;
    if (!actionData || actionType === "none") return null;

    return (
      <Grid item xs={12}>
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
          Action Data
        </Typography>
        <Box
          sx={{
            p: 1,
            border: "1px solid #ddd",
            borderRadius: 1,
            backgroundColor: "#fafafa",
          }}
        >
          {actionType === "navigate" && (
            <Typography sx={{ fontSize: "0.75rem" }}>
              Route: {actionData.route || "N/A"}
            </Typography>
          )}
          {actionType === "open_service" && (
            <Typography sx={{ fontSize: "0.75rem" }}>
              Service ID: {actionData.serviceId || "N/A"}
            </Typography>
          )}
          {actionType === "open_category" && (
            <Typography sx={{ fontSize: "0.75rem" }}>
              Category ID: {actionData.categoryId || "N/A"}
            </Typography>
          )}
          {actionType === "open_url" && (
            <Typography sx={{ fontSize: "0.75rem", wordBreak: "break-all" }}>
              URL: {actionData.url || "N/A"}
            </Typography>
          )}
          {actionType === "show_popup" && (
            <>
              <Typography sx={{ fontSize: "0.75rem" }}>
                Popup ID: {actionData.popupId || "N/A"}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem" }}>
                Content: {actionData.content || "N/A"}
              </Typography>
            </>
          )}
        </Box>
      </Grid>
    );
  };

  const renderServiceRelations = () => {
    const relations = (itemData?.serviceRelations || []).map((relation) => ({
        servicesMasterId: relation.service?.parentServiceId || relation.servicesMasterId || '',
        servicesChildId: relation.service?.id || relation.servicesMasterId || '',
        categoryId: relation.category?.parentId || '',
        subCategoryId: relation.category?.id || relation.serviceCategoryId || '',
        categoryTitle: relation.category?.parentCategory?.title || '',
        subCategoryTitle: relation.category?.title || '',
        masterServiceTitle: relation.service?.parentService?.title || '',
        childServiceTitle: relation.service?.title || '',
      })) || [];
    if (relations.length === 0) return null;

    return (
      <Grid item xs={12}>
        <Card>
          {relations.map((service, displayIndex) => {
            return (
              <Grid
                container
                key={`${service.servicesMasterId}-${service.servicesChildId}`}
                sx={{ borderBottom: "1px solid #ccc", padding: "0.5rem" }}
                spacing={2}
              >
                <Grid item xs={3}>
                  <Typography sx={{ fontWeight: "600", fontSize: "0.75rem" }}>
                    Category
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {service.subCategoryTitle}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontWeight: "600", fontSize: "0.75rem" }}>
                    Master Service
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {service.masterServiceTitle}
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography sx={{ fontWeight: "600", fontSize: "0.75rem" }}>
                    Child Service
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {service.childServiceTitle}
                  </Typography>
                </Grid>
              </Grid>
            );
          })}
        </Card>
      </Grid>
    );
  };

  return (
    <>
      {isLoading && <Loading />}
      <PageHeaderLayout
        headerTitle={`${entityLabel} Details`}
      >
        <Button
          variant="contained"
          onClick={() => navigate(sectionItemsPath)}
          sx={{ height: "30px", marginTop: "10px" }}
          startIcon={<ArrowBack />}
        >
          Back to {entityListLabel}
        </Button>
      </PageHeaderLayout>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    Section
                    </Typography>
                    <TextField
                    disabled
                    value={itemData.section?.title || ""}
                    fullWidth
                    size="small"
                    InputProps={{ style: { fontSize: "0.75rem" } }}
                    />
                </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Title
                </Typography>
                <TextField
                  disabled
                  value={itemData.title || ""}
                  fullWidth
                  size="small"
                  InputProps={{ style: { fontSize: "0.75rem" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Subtitle
                </Typography>
                <TextField
                  disabled
                  value={itemData.subtitle || ""}
                  fullWidth
                  size="small"
                  InputProps={{ style: { fontSize: "0.75rem" } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Description
                </Typography>
                <TextField
                  disabled
                  value={itemData.description || ""}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  InputProps={{ style: { fontSize: "0.75rem" } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Display Options
                </Typography>
                <Box
                  sx={{
                    p: 1,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  {renderDisplayOptions()}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Action Type
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {itemData.actionType && (
                    <Chip
                      label={itemData.actionType?.replace(/_/g, " ")}
                      size="small"
                      sx={{
                        backgroundColor:
                          actionTypeColors[itemData.actionType]?.bg ||
                          "#f5f5f5",
                        color:
                          actionTypeColors[itemData.actionType]?.color ||
                          "#757575",
                        fontSize: "0.75rem",
                        textTransform: "capitalize",
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Position
                </Typography>
                <TextField
                  disabled
                  value={itemData.position || ""}
                  fullWidth
                  size="small"
                  InputProps={{ style: { fontSize: "0.75rem" } }}
                />
              </Grid>
              {renderActionData()}
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Badge Text
                </Typography>
                <TextField
                  disabled
                  value={itemData.badgeText || ""}
                  fullWidth
                  size="small"
                  InputProps={{ style: { fontSize: "0.75rem" } }}
                />
              </Grid>
              {itemData.badgeText && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Badge Color
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: itemData.badgeColor || "#FF6B35",
                          border: "1px solid #ddd",
                          borderRadius: 0.5,
                        }}
                      />
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {itemData.badgeColor || "#FF6B35"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Badge Text Color
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: itemData.displayOptions?.badgeTextColor || "#FFFFFF",
                          border: "1px solid #ddd",
                          borderRadius: 0.5,
                        }}
                      />
                      <Typography sx={{ fontSize: "0.75rem" }}>
                        {itemData.displayOptions?.badgeTextColor || "#FFFFFF"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Badge Position
                    </Typography>
                    <TextField
                      disabled
                      value={itemData.badgePosition?.replace(/-/g, " ") || ""}
                      fullWidth
                      size="small"
                      InputProps={{ style: { fontSize: "0.75rem" } }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Card Background Color
                </Typography>
                {itemData.cardBackgroundColor ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: itemData.cardBackgroundColor,
                        border: "1px solid #ddd",
                        borderRadius: 0.5,
                      }}
                    />
                    <Typography sx={{ fontSize: "0.75rem" }}>
                      {itemData.cardBackgroundColor}
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
                    N/A
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Card Border Color
                </Typography>
                {itemData.cardBorderColor ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: itemData.cardBorderColor,
                        border: "1px solid #ddd",
                        borderRadius: 0.5,
                      }}
                    />
                    <Typography sx={{ fontSize: "0.75rem" }}>
                      {itemData.cardBorderColor}
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
                    N/A
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Aspect Ratio
                </Typography>
                <TextField
                  disabled
                  value={itemData.aspectRatio || ""}
                  fullWidth
                  size="small"
                  InputProps={{ style: { fontSize: "0.75rem" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Status
                </Typography>
                <Chip
                  label={itemData.isActive ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    backgroundColor: itemData.isActive ? "#e8f5e9" : "#ffebee",
                    color: itemData.isActive ? "#2e7d32" : "#c62828",
                    fontWeight: 500,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Start Date/Time
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                  {itemData.startDateTime
                    ? formatDateTime(itemData.startDateTime)
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  End Date/Time
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                  {itemData.endDateTime
                    ? formatDateTime(itemData.endDateTime)
                    : "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Created On
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                  {itemData.createdAt
                    ? formatDateTime(itemData.createdAt)
                    : "N/A"}
                </Typography>
              </Grid>
              {renderServiceRelations()}
            </Grid>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Media
            </Typography>
            {itemData.imageUrl ? (
              <Box
                sx={{
                  textAlign: "center",
                  border: "1px solid #eee",
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                }}
              >
                <Typography
                  sx={{ fontSize: "0.65rem", color: "#666", mb: 0.5 }}
                >
                  Main {itemData.displayOptions?.mediaType === "gif" ? "GIF" : "Image"}
                </Typography>
                <img
                  src={itemData.imageUrl}
                  alt="Item"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 150,
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
                  No main media
                </Typography>
              </Box>
            )}
          </Card>
          <Card sx={{ p: 2 }}>
            <PermissionGuard feature="customer-app" permission="canEdit">
              <Button
                onClick={() =>
                  navigate(`/customer-app/home-screen/section-item/${id}?sectionId=${itemData?.sectionId || ""}&pageType=${itemPageType}`)
                }
                variant="contained"
                color="warning"
                size="small"
                fullWidth
                startIcon={<Edit />}
              >
                Edit Item
              </Button>
            </PermissionGuard>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default ViewSectionItem;
